'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getGtmAttr,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import { IcDownload, Typography } from '@musinsa/mds';
import { Tooltip } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { pathsMusinsa } from '../../common';
import { useNavigate } from '../../common/hooks/useNavigate';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import {
  isCuratorCouponTooltipAlreadyShownToday,
  markCuratorCouponTooltipShownToday,
} from '../utils/curatorCouponTooltipPolicy';

interface Props {
  snsTitle: string;
  className?: string;
}

export const ShopCouponBanner = ({ snsTitle, className }: Props) => {
  const { service } = useDeviceInfo();
  const { navigate } = useNavigate();
  const [showCouponGuideTooltip, setShowCouponGuideTooltip] = useState(false);

  const enabledQueries = service === SERVICE.MUSINSA && Boolean(snsTitle);

  const { data, isSuccess } = useQuery({
    ...curatorQueries.coupon.count(snsTitle),
    enabled: enabledQueries,
  });

  const { data: profile } = useQuery({
    ...curatorQueries.profile.profileBySnsTitle(snsTitle),
    enabled: enabledQueries,
  });

  const showBanner = isSuccess && data !== undefined && data.totalCount > 0;
  const enabledTooltip = showBanner && data !== undefined && (data.availableCount ?? 0) > 0;

  useEffect(() => {
    if (!enabledTooltip || isCuratorCouponTooltipAlreadyShownToday()) {
      return;
    }
    setShowCouponGuideTooltip(true);
  }, [enabledTooltip]);

  const onCouponGuideTooltipClose = useCallback(() => {
    markCuratorCouponTooltipShownToday();
    setShowCouponGuideTooltip(false);
  }, []);

  if (!showBanner) return null;

  const nickname = profile?.nickname?.trim() ?? '';

  const handleClick = () => {
    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'collaboration_coupon_list',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: snsTitle,
        name: '큐레이터 브랜드 할인 쿠폰',
        section_name: 'curator_collaboration',
      });
      pushDataLayer('click_button', {
        section_name: 'curator_collaboration',
        section_index: '2',
        button_name: 'collaboration_coupon_list',
        index: GTM_NOTSET_STRING,
      });
    }
    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_shop',
          section_name: 'CuratorShopCoupon',
          section_title: '큐레이터 브랜드 할인 쿠폰',
          button_id: 'shop_coupon_banner',
          button_name: '큐레이터 브랜드 할인 쿠폰',
          position: -1,
        },
      });
    }
    navigate(pathsMusinsa.shopCoupon({ shopSlug: snsTitle, query: { direct: true } }));
  };

  const gtmAttrs = getGtmAttr(['impression_button'], {
    button_name: 'collaboration_coupon_list',
    index: GTM_NOTSET_STRING,
    section_name: 'curator_collaboration',
    section_index: '1',
  });

  return (
    <div
      {...gtmAttrs}
      className={twMerge(gtmAttrs.className, 'relative px-4 pt-4 pb-1 bg-[#f5f5f5]', className)}
    >
      {enabledTooltip && showCouponGuideTooltip && (
        <Tooltip
          className="absolute! left-1/2! top-full! z-50! -translate-x-1/2! whitespace-nowrap!"
          align="bottom center"
          content="큐레이터가 할인 쿠폰을 드려요!"
          duration={3000}
          onCloseCallback={onCouponGuideTooltipClose}
        />
      )}
      <div
        className="pointer-events-none absolute right-[54px] top-[13px] z-[2] h-[3px] w-[7px] bg-[#f5f5f5]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-px right-[54px] z-[2] h-[3px] w-[7px] bg-[#f5f5f5]"
        aria-hidden
      />
      <button
        type="button"
        onClick={handleClick}
        className="flex h-10 w-full cursor-pointer items-center rounded border border-[rgba(36,94,255,0.4)]! bg-[rgba(36,94,255,0.05)]! p-0"
      >
        <div className="flex min-w-0 flex-1 items-center px-3">
          <Typography variant="body13pxMed">
            큐레이터 {nickname ? `${nickname} ` : ''}브랜드{' '}
            <span className="text-[#245EFF]!">할인 쿠폰</span>
          </Typography>
        </div>
        <div className="relative flex h-full w-px shrink-0 items-center justify-center before:absolute before:left-1/2 before:-top-[3px] before:-ml-[3px] before:size-[6px] before:rounded-full before:border! before:border-[rgba(36,94,255,0.4)]! before:bg-[#f5f5f5]! before:content-[''] after:absolute after:-bottom-[3px] after:left-1/2 after:-ml-[3px] after:size-[6px] after:rounded-full after:border! after:border-[rgba(36,94,255,0.4)]! after:bg-[#f5f5f5]! after:content-['']">
          <div className="h-[30px] border-l! border-dashed! border-[rgba(36,94,255,0.4)]!" />
        </div>
        <div className="flex h-full w-10 shrink-0 items-center justify-center">
          <IcDownload height={20} strokeColor="blue" strokeWidth={1.25} width={20} />
        </div>
      </button>
    </div>
  );
};
